import type { CreaseAssignment } from './types';
import { MAX_FOLD_ANGLE, clamp, wrapAngle } from '@/constants/numeric';

/**
 * THE FOLD-ANGLE CONVENTION (one place, one definition).
 *
 * The flat crease pattern lies in the world XY plane, seen from +Z. Every face
 * loop is stored counter-clockwise in that view, so every facet's normal is +Z
 * when the sheet is unfolded.
 *
 * The fold angle (dihedral angle) rho of a crease is the signed angle by which
 * the two incident facets deviate from coplanar:
 *
 *   rho =  0    unfolded, the two facets are coplanar
 *   rho >  0    VALLEY: the facets rotate toward the viewer (+Z)
 *   rho <  0    MOUNTAIN: the facets rotate away from the viewer (-Z)
 *   rho = +-pi  flat-folded
 *
 * This matches the FOLD specification's `edges_foldAngle` field, which is in
 * degrees with the same sign convention, and matches the convention used in
 * Tachi's rigid-origami papers.
 *
 * The measurement formula and its gradient live in
 * src/simulation/dihedral.ts - they are the only other place that is allowed to
 * know about the sign.
 */

/** Fully-folded target angle for an assignment, in radians. */
export function nominalFoldAngle(assignment: CreaseAssignment): number {
  switch (assignment) {
    case 'V':
      return MAX_FOLD_ANGLE;
    case 'M':
      return -MAX_FOLD_ANGLE;
    default:
      return 0;
  }
}

/** Resolve the stored (possibly null) target angle for an edge. */
export function resolveTargetAngle(
  assignment: CreaseAssignment,
  stored: number | null | undefined,
): number {
  if (stored === null || stored === undefined) return nominalFoldAngle(assignment);
  return clamp(stored, -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
}

/**
 * Infer an assignment from a signed fold angle. Used when importing a FOLD file
 * that carries angles but not assignments.
 */
export function assignmentFromAngle(angle: number, tol = 1e-9): CreaseAssignment {
  if (Math.abs(angle) <= tol) return 'F';
  return angle > 0 ? 'V' : 'M';
}

/** Flip mountain <-> valley (and negate any explicit target angle). */
export function flipAssignment(a: CreaseAssignment): CreaseAssignment {
  if (a === 'M') return 'V';
  if (a === 'V') return 'M';
  return a;
}

/**
 * Scale a target fold angle by the global fold-amount slider.
 *
 * `amount` runs from -1 to +1. At 0 the sheet is flat. At +1 every crease sits
 * at its full target. Negative amounts fold the *inverse* pattern (mountains
 * become valleys), which is the standard behaviour of origami visualisers and
 * is useful for checking that a mechanism is symmetric.
 */
export function scaledTargetAngle(target: number, amount: number): number {
  return clamp(target * amount, -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
}

export { wrapAngle };
